import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Form, Button, Row, Col, InputGroup } from 'react-bootstrap';
import { resetPasswordRequest } from '../../store/account/userActions';
import { setAlertAction } from '../../store/alert/alertActions';
import { NavLink } from 'react-router-dom';
import './ResetPassword.scss';
import { Eye, EyeOff} from 'lucide-react';

const params = new Proxy(new URLSearchParams(window.location.search), {
  get: (searchParams, prop) => searchParams.get(prop),
});

function ResetPassword({ resetpassword, setAlert, history }) {
  const [data, setData] = useState({
    password: '',
    confirmPassword: '',
    resetToken: params.token,
  });

  const [errors, setErrors] = useState({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const validatePassword = (password) => {
    const validations = [
      { rule: /.{8,}/, message: 'At least 8 characters' },
      { rule: /[A-Z]/, message: 'One uppercase letter' },
      { rule: /[a-z]/, message: 'One lowercase letter' },
      { rule: /\d/, message: 'One digit' },
      { rule: /[@$!%*?&]/, message: 'One special character (@$!%*?&)' },
      { rule: /^[A-Za-z0-9@$!%*?&]+$/, message: 'Only @$!%*?& allowed', customValid: true },
    ];

    return validations.map(({ rule, message, customValid }) => ({
      message,
      isValid: customValid ? rule.test(password) : rule.test(password),
    }));
  };

  const validate = () => {
    const newErrors = {};

    const passwordRules = validatePassword(data.password);
    const invalids = passwordRules.filter(v => !v.isValid);
    if (!data.password) newErrors.password = 'Password required';
    else if (invalids.length > 0)
      newErrors.password = invalids.map(v => v.message).join(', ');

    if (!data.confirmPassword) newErrors.confirmPassword = 'Confirm your password';
    else if (data.password !== data.confirmPassword)
      newErrors.confirmPassword = 'Passwords do not match';

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length === 0) {
      resetpassword({
        password: data.password,
        resetToken: data.resetToken,
        history,
      });
    } else {
      setErrors(validationErrors);
      setAlert({
        title: 'Error',
        text: Object.values(validationErrors),
        variant: 'danger',
        outTimeMS: 3000,
      });
    }
  };

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <Row className="reset-password-page justify-content-center align-items-center">
      <Col xs={11} md={6} lg={5} xl={4}>
        <div className="reset-card p-4 shadow-sm">
          <h2 className="text-center mb-3">Reset Your Password</h2>
          <p className="text-center text-muted mb-4">Create a new password for your account</p>
          <Form onSubmit={handleSubmit}>

            <Form.Label className="mt-2">New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={passwordVisible ? 'text' : 'password'}
                name="password"
                value={data.password}
                onChange={handleChange}
                isInvalid={!!errors.password}
                placeholder="Enter new password"
              />
              <Button
                variant="outline-primary"
                class="rounded"
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                {passwordVisible ? <EyeOff /> : <Eye />}
              </Button>
              <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
            </InputGroup>

            <Form.Label className="mt-3">Confirm Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={confirmPasswordVisible ? 'text' : 'password'}
                name="confirmPassword"
                value={data.confirmPassword}
                onChange={handleChange}
                isInvalid={!!errors.confirmPassword}
                placeholder="Confirm password"
              />
              <Button
                variant="outline-primary"
                class="rounded"
                onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
              >
                {confirmPasswordVisible ? <EyeOff /> : <Eye />}
              </Button>
              <Form.Control.Feedback type="invalid">{errors.confirmPassword}</Form.Control.Feedback>
            </InputGroup>

            <ul className="password-check mt-3">
              {validatePassword(data.password).map((v, i) => (
                <li key={i} className={v.isValid ? 'valid' : 'invalid'}>
                  {v.isValid ? '✔' : '✖'} {v.message}
                </li>
              ))}
            </ul>

            <Button type="submit" className="w-100 mt-4 btn-reset">
              Reset Password
            </Button>
          </Form>

          <div className="mt-4 text-center">
            <span className="text-muted">Don’t have an account?</span>{' '}
            <NavLink to="/signup" className="text-primary fw-bold">Sign up</NavLink>
          </div>
        </div>
      </Col>
    </Row>
  );
}

const mapDispatchToProps = (dispatch) => ({
  resetpassword: (creds) => dispatch(resetPasswordRequest(creds)),
  setAlert: (payload) => dispatch(setAlertAction(payload)),
});

export default connect(null, mapDispatchToProps)(ResetPassword);
